// Texas Heart Institute Intraoperative Insulin Protocol for Cardiac Surgery,
// restructured for the in-app guide. Source PDF: public/thi/thi-insulin-protocol.pdf

export const INSULIN_PDF = '/thi/thi-insulin-protocol.pdf';

export const INSULIN_FACTS = [
  'Regular insulin, 1 unit/mL',
  'Starting rate: blood glucose ÷ 100 units/hr, rounded to the nearest 0.5',
  'Recheck glucose every 30 minutes, or every 60 minutes once stable',
];

/** Starting an infusion: glucose band -> infusion / bolus */
export const START = [
  { max: 160, range: '≤ 160', infusion: 'No infusion', bolus: null },
  { max: 180, range: '161–180', infusion: 'Consider an infusion in diabetic patients', bolus: null },
  { max: 200, range: '181–200', infusion: 'Start the infusion', bolus: null },
  { max: 250, range: '201–250', infusion: 'Start the infusion', bolus: '3–4 units', discuss: true },
  { max: Infinity, range: '≥ 251', infusion: 'Start the infusion', bolus: '5–8 units', discuss: true },
];

/**
 * Adjusting a running infusion. For each glucose band, the action depends on the
 * trend since the previous reading: rising, falling by 20 or less, falling by more
 * than 20. An unchanged reading counts as "falling by 20 or less".
 */
export const TRENDS = [
  { id: 'up', label: 'Rising' },
  { id: 'downSmall', label: 'Falling ≤ 20' },
  { id: 'downLarge', label: 'Falling > 20' },
];

export const ADJUST = [
  { max: 140, range: '≤ 140', hold: true, action: 'Hold the infusion. Recheck every 30 minutes and give D50 as indicated', bolus: null },
  { max: 160, range: '141–160', by: { up: 'No change', downSmall: 'Decrease by 1–2 units/hr', downLarge: 'Hold the infusion' }, bolus: null },
  { max: 200, range: '161–200', by: { up: 'No change', downSmall: 'No change', downLarge: 'Decrease by 1–2 units/hr' }, bolus: null },
  { max: 250, range: '201–250', by: { up: 'Increase by 1–2 units/hr', downSmall: 'Increase by 1–2 units/hr', downLarge: 'No change' }, bolus: '3 units', discuss: true },
  { max: Infinity, range: '≥ 251', by: { up: 'Increase by 3 units/hr', downSmall: 'Increase by 3 units/hr', downLarge: 'No change' }, bolus: '5 units', discuss: true },
];

export const INSULIN_NOTES = [
  'If hyperkalemia is treated with D50 and insulin, stop the protocol. Consider restarting one hour after the last treatment.',
];

export const INSULIN_BACKGROUND = [
  'The group created this protocol after noticing more deep sternal wound infections following sternotomy, alongside several notable perioperative hypoglycemic events. The goal is to keep blood glucose in an acceptable range while avoiding hypoglycemia, which is more harmful than hyperglycemia. Outcomes are tracked and the protocol adjusted as needed.',
];

export const INSULIN_REFERENCES = [
  'Finfer S, Chittock DR, Su SY, et al. Intensive versus conventional glucose control in critically ill patients. N Engl J Med 2009;360:1283-1297.',
  'Furnary AP, Gao G, Grunkemeier GL, et al. Continuous insulin infusion reduces mortality in patients with diabetes undergoing coronary artery bypass grafting. J Thorac Cardiovasc Surg 2003;125:1007-1021.',
  'Gandhi GY, Nuttall GA, Abel MD, et al. Intensive intraoperative insulin therapy versus conventional glucose management during cardiac surgery. Ann Intern Med 2007;146:233-243.',
  'Gatti G, Perrotti A, Reichart D, et al. Glycated hemoglobin and risk of sternal wound infection after isolated coronary surgery. Circ J 2017;81:36-43.',
  'Kramer R, Groom R, Weldner D, et al. Glycemic control and reduction of deep sternal wound infection rates. Arch Surg 2008;143:451-456.',
  'Lazar HL, McDonnell M, Chipkin SR, et al. The Society of Thoracic Surgeons practice guideline series: blood glucose management during adult cardiac surgery. Ann Thorac Surg 2009;87:663-669.',
  'Duggan EW, Carlson K, Umpierrez GE. Perioperative hyperglycemia management. Anesthesiology 2017;126:547-560.',
];

/** Helpers used by the calculator */
export const startingRate = (glucose) => (glucose > 160 ? Math.round((glucose / 100) * 2) / 2 : 0);
export const bandFor = (table, glucose) => table.find((b) => glucose <= b.max);
export const trendFor = (current, previous) => {
  const change = current - previous;
  if (change > 0) return { id: 'up', change };
  if (change < -20) return { id: 'downLarge', change };
  return { id: 'downSmall', change };
};
